import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import { Canceler } from 'axios';

import { CancelToken } from '@rexlabs/api-client';
import Box from '@rexlabs/box';
import { withModel } from '@rexlabs/model-generator';
import { styled, StyleSheet, Styles } from '@rexlabs/styling';

import Panel from '../panel';
import MonthRangeSlider, { MonthYearType } from '../slider/month-range-slider';
import { ToggleButtonGroup } from '../toggle-button';
import { Loading } from '../graph';

import { ReportSettingsContext } from 'src/features/reports/providers/report-settings-provider';
import { compose } from 'utils/compose';
import productTrendsModel from 'src/features/reports/models/product-trends';
import { ProductTrendsModel } from 'types/models/product-trends';
import { TimeOnMarketSummary, SizeBandData } from 'types/graph';
import withError from 'view/containers/with-error';
import { WithErrorModalProps } from 'types/hoc/with-error-modal';
import { Auth0Context } from 'src/auth0-provider';
import { TRENDS_PAGE } from 'src/features/reports/constants';
import { COLORS } from 'src/features/reports/theme';
import InfoTooltip from './info-tooltip';

const CALCULATION = [
  { label: 'Median', key: 'median' },
  { label: 'Average', key: 'average' }
];

const SIZE_BANDS: (keyof TimeOnMarketSummary)[] = [
  '<300',
  '300-350',
  '351-512',
  '>512'
];

const SIZE_BAND_LABELS: Record<string, string> = {
  '<300': '< 300 SQM',
  '300-350': '300-350 SQM',
  '351-512': '351-512 SQM',
  '>512': '> 512 SQM'
};

const defaultStyles = StyleSheet({
  sizeBandBox: {
    width: 160,
    height: 100,
    backgroundColor: COLORS.WHITE,
    border: `1px solid ${COLORS.GREY.BORDER}`,
    borderRadius: 8,
    padding: '0 10px',
    textAlign: 'center',
    userSelect: 'none'
  },
  sizeBandLabel: {
    fontFamily: 'DM Sans',
    fontSize: '12px',
    color: COLORS.GREY.MEDIUM,
    letterSpacing: '0.5px'
  },
  sizeBandValue: {
    fontFamily: 'DM Sans',
    fontSize: '28px',
    fontWeight: 500,
    color: COLORS.BLUE.DARK,
    lineHeight: 2
  },
  sizeBandSold: {
    fontFamily: 'DM Sans',
    fontSize: '11px',
    color: COLORS.GREY.MEDIUM
  }
});

const EMPTY_SUMMARY: TimeOnMarketSummary = {
  '<300': null,
  '300-350': null,
  '351-512': null,
  '>512': null,
  all: null
};

interface TimeOnMarketBySizeProps {
  productTrendsModel: ProductTrendsModel;
  errorModal: WithErrorModalProps;
  styles: Styles;
}

const TimeOnMarketBySize = ({
  productTrendsModel,
  errorModal: { open: openError },
  styles: s
}: TimeOnMarketBySizeProps) => {
  const { hasPermission } = useContext(Auth0Context);
  const { period, selectedProjectIds, subRegionsPermissions, isFirstLoad } =
    useContext(ReportSettingsContext);
  const canceler = useRef<Canceler | null>(null);
  const [data, setData] = useState<TimeOnMarketSummary>(EMPTY_SUMMARY);
  const [isLoading, setIsLoading] = useState(false);
  const [calculation, setCalculation] = useState('median');

  const [dateFilter, setDateFilter] = useState<MonthYearType>({
    year: 0,
    month: 0
  });

  // Default to 6 months back from period
  useEffect(() => {
    if (!period) return;
    const defaultStart = moment(period).subtract(6, 'months');
    setDateFilter({
      year: defaultStart.year(),
      month: defaultStart.month() + 1
    });
  }, [period]);

  const { startPeriod, endPeriod } = useMemo(() => {
    if (!period || dateFilter.month === 0 || dateFilter.year === 0) {
      return { startPeriod: null, endPeriod: null };
    }
    return {
      startPeriod: moment(`${dateFilter.year}-${dateFilter.month}-1`).format(
        'YYYY-MM-DD'
      ),
      endPeriod: moment(period).format('YYYY-MM-DD')
    };
  }, [dateFilter, period]);

  useEffect(() => {
    if (
      (!selectedProjectIds.length || !startPeriod || !endPeriod) &&
      !isFirstLoad
    ) {
      return;
    }

    canceler.current?.();
    const cancelToken = new CancelToken((c: Canceler) => {
      canceler.current = c;
    });

    setIsLoading(true);
    productTrendsModel
      .fetchTimeOnMarketBySizeBand({
        queryParams: {
          project_ids: selectedProjectIds,
          start_period: startPeriod,
          end_period: endPeriod
        },
        hasPermission:
          !isFirstLoad &&
          hasPermission([...subRegionsPermissions, TRENDS_PAGE]),
        config: { cancelToken }
      })
      .then((res) => {
        if (!res.data) return;
        setData(res.data);
        setIsLoading(false);
      })
      .catch((err) => {
        setIsLoading(false);
        openError(err);
      });

    return () => {
      canceler.current?.();
    };
  }, [
    selectedProjectIds,
    startPeriod,
    endPeriod,
    productTrendsModel,
    openError,
    hasPermission,
    subRegionsPermissions,
    isFirstLoad
  ]);

  return (
    <Panel
      title="Days on Market by Size"
      tooltip={{
        title: 'Days on Market by Size',
        description: <InfoTooltip />
      }}
    >
      <Loading isLoading={isLoading} />
      <Box justifyContent="center" pt="12px" mb="12px">
        <ToggleButtonGroup
          value={[calculation]}
          group={CALCULATION}
          onToggle={(key: string) => setCalculation(key)}
        />
      </Box>
      <Box justifyContent="center" pt="12px" mb="24px" style={{ gap: 20 }}>
        {SIZE_BANDS.map((band) => {
          const bandData: SizeBandData | null = data[band];
          return (
            <Box
              key={band}
              {...s('sizeBandBox')}
              flexDirection="column"
              justifyContent="center"
              alignItems="center"
            >
              <Box {...s('sizeBandLabel')}>{SIZE_BAND_LABELS[band]}</Box>
              <Box {...s('sizeBandValue')}>
                {bandData
                  ? `${Math.round(
                      calculation === 'median'
                        ? bandData.median_days
                        : bandData.avg_days
                    )}d`
                  : '\u2014'}
              </Box>
              {bandData && (
                <Box {...s('sizeBandSold')}>{bandData.sold_count} sold</Box>
              )}
            </Box>
          );
        })}
        {(() => {
          const allData = data.all;
          const daysValue = allData
            ? calculation === 'median'
              ? allData.median_days
              : allData.avg_days
            : null;
          return (
            <Box
              {...s('sizeBandBox')}
              flexDirection="column"
              justifyContent="center"
              alignItems="center"
            >
              <Box {...s('sizeBandLabel')}>ALL LOTS</Box>
              <Box {...s('sizeBandValue')}>
                {daysValue != null ? `${Math.round(daysValue)}d` : '\u2014'}
              </Box>
              {allData && (
                <Box {...s('sizeBandSold')}>{allData.sold_count} sold</Box>
              )}
            </Box>
          );
        })()}
      </Box>
      <Box width="588px" m="0 auto 34px" style={{ userSelect: 'none' }}>
        <MonthRangeSlider
          value={dateFilter}
          onChange={(value: MonthYearType) => setDateFilter(value)}
          numOfMonthsBack={11}
          latestMonth={period}
        />
      </Box>
    </Panel>
  );
};

export default compose(
  styled(defaultStyles),
  withModel(productTrendsModel),
  withError.withPropName('errorModal')
)(TimeOnMarketBySize);
